import {
  MAX_SYNC_SELECTION,
  type CatalogSyncCandidate,
  type CatalogSyncRun,
  type CatalogSyncSummary,
} from '@avrahami/contracts';
import {
  Alert,
  Badge,
  Button,
  Card,
  CategoryBrowser,
  Cell,
  CheckboxField,
  ConfirmDialog,
  countText,
  formatDate,
  formatDateTime,
  hasGroupedProducts,
  isApiError,
  LoadMore,
  Loading,
  PageHeader,
  setValues,
  TextField,
  toggleValue,
  returnToPrevious,
  useListFilters,
} from '@avrahami/ui';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactNode, type SyntheticEvent } from 'react';
import { useCan } from '../api/session';
import {
  catalogSyncCandidatesQuery,
  catalogSyncGroupsQuery,
  catalogSyncSummaryQuery,
  keys,
  NO_CATALOG_SYNC_FILTERS,
  tenantApi,
  useStoreTimeZone,
  type CatalogSyncFilters,
} from '../api/tenant';
import { Thumbnail } from '../components/fields';
import { fill, he } from '../i18n/he';
import { errorMessage } from '../lib/forms';
import { catalogSyncMediaUrl } from '../lib/media';
import { formatMoney } from '../lib/money';

const LINK_CLASS = 'font-medium text-brand-700 hover:underline';

/**
 * "Pull new products": the catalog products the store does not have yet, to add all or some of.
 * A pull only adds (as drafts); it never changes a product the store already has.
 *
 * The products are shown under the catalog's categories: first the categories, a category opens
 * to its sub-categories, and only a sub-category shows products. A search shows its results at
 * once. What is ticked is kept across categories and searches.
 */
export function ProductPullPage() {
  const canReadProducts = useCan('store.products.read');
  const summary = useQuery(catalogSyncSummaryQuery());
  return (
    <>
      {canReadProducts ? (
        <Link
          to="/products"
          onClick={returnToPrevious}
          className="mb-2 inline-block text-sm text-brand-700 hover:underline"
        >
          {he.products.back}
        </Link>
      ) : null}
      <PageHeader title={he.productPull.title} />
      {summary.isPending ? <Loading /> : null}
      {summary.isError ? <Alert>{errorMessage(summary.error)}</Alert> : null}
      {summary.isSuccess ? (
        <ProductPull summary={summary.data} canReadProducts={canReadProducts} />
      ) : null}
    </>
  );
}

function ProductPull({
  summary,
  canReadProducts,
}: {
  summary: CatalogSyncSummary;
  canReadProducts: boolean;
}) {
  const queryClient = useQueryClient();
  const timeZone = useStoreTimeZone();
  const { draft, filters, change, apply, applyNow } = useListFilters(
    'product-pull',
    NO_CATALOG_SYNC_FILTERS,
  );
  // Kept across searches: the owner can search, tick, search again and add everything ticked.
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [confirmingAll, setConfirmingAll] = useState(false);
  const searching = filters.q.trim() !== '';
  const groups = useQuery({ ...catalogSyncGroupsQuery(filters.onlyNew), enabled: !searching });

  // The pull being followed: one this screen started, or one found running. Its id tells its end
  // apart from an older pull that the summary still shows as the last one.
  const followed = useRef<string | null>(null);
  const activeId = summary.activeRun?.id ?? null;
  const lastId = summary.lastRun?.id ?? null;
  useEffect(() => {
    if (activeId !== null) {
      followed.current = activeId;
      return;
    }
    if (followed.current === null || followed.current !== lastId) return;
    // It ended: the store has new products (and maybe categories), and fewer to offer.
    followed.current = null;
    setSelected([]);
    for (const queryKey of [
      keys.catalogSync(),
      keys.products(),
      keys.categories(),
      keys.dashboard(),
    ]) {
      void queryClient.invalidateQueries({ queryKey });
    }
  }, [activeId, lastId, queryClient]);

  const start = useMutation({
    mutationFn: tenantApi.startCatalogSync,
    // Nothing is assumed about the pull: the summary is read again and followed while it runs.
    onSuccess: async (run) => {
      setConfirmingAll(false);
      followed.current = run.id;
      await queryClient.invalidateQueries({ queryKey: keys.catalogSyncSummary() });
    },
    onError: async (error) => {
      setConfirmingAll(false);
      // Some of the selected products left the catalog: show the list as it is now.
      if (isApiError(error, 'BAD_REQUEST')) setSelected([]);
      if (isApiError(error, 'BAD_REQUEST') || isApiError(error, 'CONFLICT')) {
        await queryClient.invalidateQueries({ queryKey: keys.catalogSync() });
      }
    },
  });

  const busy = summary.activeRun !== null || start.isPending;
  const overLimit = selected.length > MAX_SYNC_SELECTION;
  const run = summary.activeRun ?? summary.lastRun;
  const chosen = new Set(selected);
  const candidates = (categoryId: string) => (
    <Candidates
      filters={filters}
      categoryId={categoryId}
      chosen={chosen}
      timeZone={timeZone}
      onToggle={(id) => {
        setSelected((current) => toggleValue(current, id));
      }}
      onSet={(ids, on) => {
        setSelected((current) => setValues(current, ids, on));
      }}
    />
  );

  const onFilter = (event: SyntheticEvent) => {
    event.preventDefault();
    apply();
  };

  return (
    <div className="flex flex-col gap-6">
      <Card title={he.productPull.summary}>
        <div className="flex flex-col gap-4" data-testid="sync-summary">
          <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
            <dt className="text-slate-500">{he.productPull.candidates}</dt>
            <dd data-testid="sync-candidates-count">{summary.candidates}</dd>
            <dt className="text-slate-500">{he.productPull.newSinceLastPull}</dt>
            <dd data-testid="sync-new-count">{summary.newSinceLastPull}</dd>
            <dt className="text-slate-500">{he.productPull.lastPulledAt}</dt>
            <dd>
              {summary.lastPulledAt === null
                ? he.productPull.neverPulled
                : formatDateTime(summary.lastPulledAt, timeZone)}
            </dd>
          </dl>
          <p className="text-sm font-medium text-slate-700" data-testid="sync-automatic">
            {he.productPull.automatic}
          </p>
          <p className="text-sm text-slate-600">{he.productPull.principle}</p>
        </div>
      </Card>

      {summary.discontinued > 0 ? (
        <Alert tone="warning">
          <div data-testid="sync-discontinued">
            {countText(summary.discontinued, {
              one: he.productPull.discontinuedOne,
              many: he.productPull.discontinuedMany,
            })}{' '}
            {he.productPull.discontinuedHint}{' '}
            {canReadProducts ? (
              <Link
                to="/products"
                search={{ sourceState: 'SOURCE_DISCONTINUED' }}
                className="font-medium underline"
                data-testid="sync-discontinued-link"
              >
                {he.productPull.discontinuedLink}
              </Link>
            ) : null}
          </div>
        </Alert>
      ) : null}
      {summary.removed > 0 ? (
        <p className="text-sm text-slate-500" data-testid="sync-removed">
          {countText(summary.removed, {
            one: he.productPull.removedOne,
            many: he.productPull.removedMany,
          })}{' '}
          {canReadProducts ? (
            <Link
              to="/products"
              search={{ status: 'ARCHIVED' }}
              className={LINK_CLASS}
              data-testid="sync-removed-link"
            >
              {he.productPull.removedLink}
            </Link>
          ) : null}
        </p>
      ) : null}

      {run === null ? null : (
        <RunStatus
          run={run}
          active={summary.activeRun !== null}
          timeZone={timeZone}
          canReadProducts={canReadProducts}
        />
      )}

      <Card title={he.productPull.list}>
        <form onSubmit={onFilter} className="mb-4 flex flex-wrap items-end gap-3">
          <TextField
            label={he.productPull.search}
            name="q"
            type="search"
            className="w-72"
            maxLength={100}
            data-testid="sync-search"
            value={draft.q}
            onChange={(event) => {
              change({ q: event.target.value });
            }}
          />
          <Button type="submit" tone="secondary">
            {he.common.search}
          </Button>
          <CheckboxField
            label={he.productPull.onlyNew}
            name="onlyNew"
            className="self-center"
            data-testid="sync-only-new"
            checked={draft.onlyNew}
            onChange={(event) => {
              // A tick applies at once (with the text already searched), without "search".
              applyNow({ onlyNew: event.target.checked });
            }}
          />
        </form>

        {searching ? (
          candidates('')
        ) : (
          <>
            {groups.isPending ? <Loading /> : null}
            {groups.isError ? <Alert>{errorMessage(groups.error)}</Alert> : null}
            {groups.data === undefined ? null : hasGroupedProducts(groups.data) ? (
              <CategoryBrowser groups={groups.data} renderProducts={candidates} />
            ) : (
              <p className="text-sm text-slate-500" data-testid="sync-empty">
                {filters.onlyNew ? he.productPull.noResults : he.productPull.empty}
              </p>
            )}
          </>
        )}

        <div className="mt-4 flex flex-col gap-3">
          {start.isError ? (
            <div data-testid="sync-error">
              <Alert>
                {errorMessage(start.error, {
                  CONFLICT: he.productPull.conflict,
                  BAD_REQUEST: he.productPull.unavailable,
                })}
              </Alert>
            </div>
          ) : null}
          {overLimit ? (
            <p className="text-xs text-amber-700" data-testid="sync-over-limit">
              {fill(he.productPull.overLimit, { max: MAX_SYNC_SELECTION })}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="sync-add-selected"
              disabled={busy || selected.length === 0 || overLimit}
              onClick={() => {
                start.mutate({ selection: 'SELECTED', masterProductIds: [...selected] });
              }}
            >
              {fill(he.productPull.addSelected, { count: selected.length })}
            </Button>
            <Button
              tone="secondary"
              data-testid="sync-add-all"
              disabled={busy || summary.candidates === 0}
              onClick={() => {
                start.reset();
                setConfirmingAll(true);
              }}
            >
              {fill(he.productPull.addAll, { count: summary.candidates })}
            </Button>
            {selected.length > 0 ? (
              <Button
                tone="secondary"
                data-testid="sync-clear-selection"
                onClick={() => {
                  setSelected([]);
                }}
              >
                {he.productPull.clearSelection}
              </Button>
            ) : null}
          </div>
        </div>
      </Card>

      <ConfirmDialog
        open={confirmingAll}
        title={he.productPull.confirmTitle}
        confirmLabel={he.productPull.confirm}
        confirmTestId="sync-confirm"
        busy={start.isPending}
        onConfirm={() => {
          start.mutate({ selection: 'ALL' });
        }}
        onClose={() => {
          setConfirmingAll(false);
        }}
      >
        {countText(summary.candidates, {
          one: he.productPull.confirmOne,
          many: he.productPull.confirmMany,
        })}
      </ConfirmDialog>
    </div>
  );
}

/** The pull that is running, or the last one that ended. */
function RunStatus({
  run,
  active,
  timeZone,
  canReadProducts,
}: {
  run: CatalogSyncRun;
  active: boolean;
  timeZone: string;
  canReadProducts: boolean;
}) {
  const added =
    run.productsAdded === 0
      ? he.productPull.runAddedNone
      : countText(run.productsAdded, {
          one: he.productPull.runAddedOne,
          many: he.productPull.runAddedMany,
        });
  const categories =
    run.categoriesAdded === 0
      ? ''
      : ` ${countText(run.categoriesAdded, {
          one: he.productPull.runCategoriesOne,
          many: he.productPull.runCategoriesMany,
        })}`;
  // The platform's own pull of the products new in the catalog (adr/0042).
  const automatic = run.selection === 'NEW';
  const title = active
    ? automatic
      ? he.productPull.runActiveAutomatic
      : he.productPull.runActive
    : automatic
      ? he.productPull.runLastAutomatic
      : he.productPull.runLast;
  return (
    <Card title={title}>
      <div data-testid="sync-run-status" data-status={run.status} className="flex flex-col gap-2">
        {run.status === 'PENDING' || run.status === 'RUNNING' ? (
          <>
            <Loading />
            <p className="text-sm text-slate-700" data-testid="sync-run-progress">
              {fill(he.productPull.runProgress, {
                added: run.productsAdded,
                requested: run.productsRequested,
              })}
            </p>
          </>
        ) : null}
        {run.status === 'SUCCEEDED' ? (
          <Alert tone="success">
            <span data-testid="sync-run-result">{`${added}${categories}`}</span>
            {run.finishedAt === null
              ? null
              : ` · ${fill(he.productPull.runFinishedAt, {
                  when: formatDateTime(run.finishedAt, timeZone),
                })}`}
            {canReadProducts && run.productsAdded > 0 ? (
              <>
                {' · '}
                <Link
                  to="/products"
                  search={{ status: 'DRAFT' }}
                  className="font-medium underline"
                  data-testid="sync-drafts-link"
                >
                  {he.productPull.runDraftsLink}
                </Link>
              </>
            ) : null}
          </Alert>
        ) : null}
        {run.status === 'FAILED' ? (
          <Alert>
            {fill(he.productPull.runFailed, {
              added: run.productsAdded,
              requested: run.productsRequested,
            })}
          </Alert>
        ) : null}
      </div>
    </Card>
  );
}

/**
 * The candidates of one category of the catalog, or (without a category) the results of a
 * search: a table that loads more, with a box that ticks every row loaded.
 */
function Candidates({
  filters,
  categoryId,
  chosen,
  timeZone,
  onToggle,
  onSet,
}: {
  filters: CatalogSyncFilters;
  categoryId: string;
  chosen: ReadonlySet<string>;
  timeZone: string;
  onToggle: (id: string) => void;
  onSet: (ids: readonly string[], on: boolean) => void;
}) {
  const candidates = useInfiniteQuery(catalogSyncCandidatesQuery(filters, categoryId));
  const items = candidates.data?.pages.flatMap((page) => page.items) ?? [];
  const loadedIds = items.map((item) => item.id);
  return (
    <>
      {candidates.isPending ? <Loading /> : null}
      {candidates.isError ? <Alert>{errorMessage(candidates.error)}</Alert> : null}
      {candidates.isSuccess && items.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="sync-empty">
          {he.productPull.noResults}
        </p>
      ) : null}
      {items.length > 0 ? (
        <CandidateTable
          items={items}
          chosen={chosen}
          allChosen={loadedIds.every((id) => chosen.has(id))}
          timeZone={timeZone}
          onToggle={onToggle}
          onToggleAll={(on) => {
            onSet(loadedIds, on);
          }}
        />
      ) : null}
      <LoadMore
        hasMore={candidates.hasNextPage}
        busy={candidates.isFetchingNextPage}
        onMore={() => {
          void candidates.fetchNextPage();
        }}
      />
    </>
  );
}

function HeadCell({ children }: { children: ReactNode }) {
  return <th className="px-3 py-2 font-medium">{children}</th>;
}

/**
 * The candidates, with a box per row and one in the heading for every row loaded. (The shared
 * `Table` takes text headings only, so this one lays out its own in the same style.)
 */
function CandidateTable({
  items,
  chosen,
  allChosen,
  timeZone,
  onToggle,
  onToggleAll,
}: {
  items: readonly CatalogSyncCandidate[];
  chosen: ReadonlySet<string>;
  allChosen: boolean;
  timeZone: string;
  onToggle: (id: string) => void;
  onToggleAll: (on: boolean) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-right text-sm">
        <thead className="border-b border-slate-200 text-xs text-slate-500">
          <tr>
            <HeadCell>
              <input
                type="checkbox"
                aria-label={he.productPull.selectAll}
                data-testid="sync-select-all"
                checked={allChosen}
                onChange={(event) => {
                  onToggleAll(event.target.checked);
                }}
                className="size-4 rounded border-slate-300 accent-brand-600"
              />
            </HeadCell>
            <HeadCell>{he.productPull.image}</HeadCell>
            <HeadCell>{he.productPull.name}</HeadCell>
            <HeadCell>{he.productPull.sku}</HeadCell>
            <HeadCell>{he.productPull.categories}</HeadCell>
            <HeadCell>{he.productPull.cost}</HeadCell>
            <HeadCell>{he.productPull.startPrice}</HeadCell>
            <HeadCell>{he.productPull.addedAt}</HeadCell>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.map((item) => (
            <tr key={item.id} data-testid="sync-candidate-row" data-sku={item.sku}>
              <Cell>
                <input
                  type="checkbox"
                  aria-label={`${he.productPull.select}: ${item.name}`}
                  data-testid="sync-select"
                  checked={chosen.has(item.id)}
                  onChange={() => {
                    onToggle(item.id);
                  }}
                  className="size-4 rounded border-slate-300 accent-brand-600"
                />
              </Cell>
              <Cell>
                {item.image === null ? (
                  <span className="text-xs text-slate-500">{he.productPull.noImage}</span>
                ) : (
                  <Thumbnail
                    file={item.image}
                    alt={item.name}
                    size={48}
                    urlOf={catalogSyncMediaUrl}
                  />
                )}
              </Cell>
              <Cell>
                <span className="font-medium">{item.name}</span>{' '}
                {item.isNew ? (
                  <span data-testid="sync-new-badge">
                    <Badge tone="green">{he.productPull.newBadge}</Badge>
                  </span>
                ) : null}
                {item.manufacturer === null && item.model === null ? null : (
                  <div className="text-xs text-slate-500">
                    {[item.manufacturer, item.model].filter((part) => part !== null).join(' · ')}
                  </div>
                )}
              </Cell>
              <Cell className="ltr">{item.sku}</Cell>
              <Cell>
                {item.categories.length === 0 ? he.common.none : item.categories.join(', ')}
              </Cell>
              <Cell className="whitespace-nowrap">
                {item.costMinor === null ? he.productPull.noPrice : formatMoney(item.costMinor)}
              </Cell>
              <Cell className="whitespace-nowrap">
                {item.startPriceMinor === null
                  ? he.productPull.noPrice
                  : formatMoney(item.startPriceMinor)}
              </Cell>
              <Cell className="whitespace-nowrap">{formatDate(item.addedAt, timeZone)}</Cell>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
